import React, { ReactNode, useCallback, useRef, forwardRef } from 'react';
import { mergeProps, useHover, useId, useSwitch, VisuallyHidden } from 'react-aria';
import { useToggleState } from 'react-stately';
import { FocusEvents, HoverEvents } from '@react-types/shared';
import { useLogger, Loggable } from '@direct-frontend/modules-logger';
import { isDesktop } from '@direct-frontend/platform-code';
import { castRef, type JSONValue } from '@direct-frontend/stdlib';

import { useInternalVerticalAlign } from '../../hooks/useVerticalAlign/useVerticalAlign';
import { VerticalAlignAddonSize, VerticalAlignTextPreset } from '../../hooks/useVerticalAlign/types';
import { cn } from '../../lib/css/className/className';
import { HStack } from '../Stack';
import { TextGeneric, TextTruncationProps } from '../TextGeneric/TextGeneric';
import { HelpProps } from '../Help';
import { TestProps } from '../../internal/types/testTypes';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { TextGenericWithSkin, TextProps } from '../TextGeneric/TextGenericWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { useFocusOptions } from '../../hooks/useFocusOptions';

import { SwitcherSize } from './types';
import { Control } from './Control/Control';

import './Switcher.css';

type UseSwitcherProps = Parameters<typeof useSwitch>[0];

export interface SwitcherProps
    extends Omit<
            UseSwitcherProps,
            'children' | 'isReadOnly' | 'validationState' | 'isRequired' | 'excludeFromTabOrder'
        >,
        HoverEvents,
        FocusEvents,
        TextTruncationProps,
        TestProps,
        Loggable {
    /**
     * Переключение жирного шрифта подписи
     */
    isBold?: boolean;

    /**
     * Подстрочный текст к подписи
     */
    caption?: ReactNode;

    /**
     * Компонент со всплывающей подсказкой при клике на знак вопроса.
     * При передаче данного параметра рядом с текстом рисуется иконка `Help` соответствующего размера.
     * @param helpProps - параметры для передадчи во всплывающую подсказку (управляют размером, цветом и выравниванием)
     */
    help?: (helpProps: HelpProps) => ReactNode;

    /**
     * Подпись для элемента
     */
    children?: ReactNode;

    /**
     * Дополнительный класс для элемента
     */
    className?: string;
    /**
     * размер Switcher
     * @default m
     */
    size?: SwitcherSize;
}

const cnSwitcher = cn('Switcher');

function getTextSize(props: SwitcherProps): VerticalAlignTextPreset {
    const { isBold, caption, size = 'm' } = props;
    switch (size) {
        case 's':
            if (caption) {
                return isBold ? '14-20-M + 14-20' : '14-20 + 14-20';
            }

            return isBold ? '14-20-M' : '14-20';
        case 'm':
            if (caption) {
                return isBold ? '16-24-M + 16-24' : '16-24 + 16-24';
            }

            return isBold ? '16-24-M' : '16-24';
    }
}

const addonSizeMap: Record<SwitcherSize, VerticalAlignAddonSize> = {
    s: '16',
    m: '20',
};

export const Switcher = forwardRef<HTMLElement, SwitcherProps>(function Switcher(props, forwardedRef) {
    const {
        children,
        isDisabled,
        caption,
        help,
        className,
        isTruncated,
        maxLines,
        logNode,
        onChange,
        size = 'm',
    } = props;

    const sizeSkinClass = getSizeSkin(size);

    const ref = useRef(null);
    const { LogProvider, logClick } = useLogger('Switcher', logNode);

    const handleChange = useCallback(
        (isSelected: boolean) => {
            const logData: JSONValue = {
                type: 'onChange',
                value: isSelected,
            };
            logClick(logData);

            onChange?.(isSelected);
        },
        [logClick, onChange],
    );

    const state = useToggleState({ ...props, onChange: handleChange });
    const { inputProps, labelProps } = useSwitch({ ...props, onChange: handleChange }, state, ref);
    const id = useId(props.id);
    const { hoverProps, isHovered } = useHover({ ...props, isDisabled: false });
    // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
    const { focusProps, isFocusVisible } = useFocusOptions();

    const textSizeWithSkin = 'current-main + current-body';

    // Костыль пока размеры шрифтов не поменяем DIRECT-238538
    const { addonClasses: addonClassesWithoutSkin } = useInternalVerticalAlign({
        textSize: getTextSize(props),
        addonSize: addonSizeMap[size],
    });
    const { addonClasses: addonClassesWithSkin } = useInternalVerticalAlignWithSkin({
        textSize: textSizeWithSkin,
        addonType: 'icon',
    });
    // Костыль пока размеры шрифтов не поменяем DIRECT-238538
    const addonClasses = size === 's' ? addonClassesWithoutSkin : addonClassesWithSkin;

    const dataTestId = props['data-testid'] ? `${props['data-testid']}.Switcher` : 'Switcher';

    const control = (
        <Control
            isChecked={state.isSelected}
            isHovered={isHovered}
            isFocused={isFocusVisible}
            isDisabled={isDisabled}
            size={size}
            data-testid="SwitcherControl"
        />
    );

    // Костыль пока размеры шрифтов не поменяем DIRECT-238538
    const textRenderer = useCallback(
        (textProps: Omit<TextProps, 'children' | 'textSize' | 'skinSize'>) => {
            // Костыль пока размеры шрифтов не поменяем DIRECT-238538
            if (size === 's') {
                return (
                    <TextGeneric {...textProps} textSize={getTextSize(props)}>
                        {children}
                    </TextGeneric>
                );
            }

            return (
                <TextGenericWithSkin {...textProps} bold={props.isBold} skinSize={size} textSize={textSizeWithSkin}>
                    {children}
                </TextGenericWithSkin>
            );
        },
        [children, props, size],
    );

    const content = (
        <>
            <VisuallyHidden>
                <input {...inputProps} {...focusProps} id={id} ref={ref} data-testid={`${dataTestId}.input`} />
            </VisuallyHidden>
            {children ? (
                <HStack gap={'current-geometry'} justifyContent={'space-between'}>
                    {textRenderer({
                        color: isDisabled ? 'gray-disabled' : 'gray',
                        caption: caption,
                        help: help,
                        isTruncated: isTruncated,
                        maxLines: maxLines,
                    })}
                    <div className={addonClasses}>{control}</div>
                </HStack>
            ) : (
                control
            )}
        </>
    );

    if (isDesktop()) {
        return (
            <LogProvider>
                <label
                    {...mergeProps(hoverProps, labelProps)}
                    className={cnSwitcher({ disabled: isDisabled }, [sizeSkinClass, className])}
                    ref={castRef(forwardedRef, 'label')}
                    data-testid={dataTestId}
                >
                    {content}
                </label>
            </LogProvider>
        );
    }

    return (
        <LogProvider>
            <div
                {...mergeProps(hoverProps, labelProps)}
                className={cnSwitcher({ disabled: isDisabled }, [sizeSkinClass, className])}
                ref={castRef(forwardedRef, 'div')}
                data-testid={dataTestId}
            >
                {content}
            </div>
        </LogProvider>
    );
});
